// Imports
import { Pagination, PostOrPage } from '@tryghost/content-api';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useParams, Params, useSearchParams } from 'react-router-dom';
import commonActions from '../../../../shareables/foundation/front-end/redux/actions';
import { useAppDispatch } from '../../../../shareables/foundation/front-end/redux/hooks';
import NotFound from '../../../pages/not-found';
import SimplePageTemplate from '../../../resources/simple-page-template';
import ghost from '../../../../shareables/integrations/ghost';
import Previews, { readPrerenderedPostCount } from '../previews';
import prerenderData from '../../../../utils/prerender-data';


// Define what the prerendered tag snapshot looks like
interface PrerenderedTag {
	name: string | undefined;
}


// Infer a display name from a tag slug
const inferTagName = (slug: string): string =>
	slug.replace(/-/g, ' ').replace(/^./, (character) => character.toUpperCase());


// Define the accepted props
interface Props {
	storybookCount?: number;
	storybookTagName?: string;
	storybookPosts?: PostOrPage[];
	storybookPagination?: Pagination;
}


// Function component
const Tag: React.FC<Props> = ({ storybookCount, storybookTagName, storybookPosts, storybookPagination }) => {
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Use React Router functionality
	const { tagIdentifier } = useParams<Params>();
	const [searchParams] = useSearchParams();
	const initialPageRef = useRef(parseInt(searchParams.get('page') ?? '1'));
	
	
	// Initialize
	const prerenderKey = tagIdentifier ? `blog:tag:${tagIdentifier}` : '';
	
	
	// Use state
	const prerenderedName =
		!storybookTagName && tagIdentifier ? prerenderData.read<PrerenderedTag>(prerenderKey)?.name : undefined;
	
	const prerenderedCount = storybookCount
		? undefined
		: readPrerenderedPostCount(tagIdentifier ? [tagIdentifier] : [], initialPageRef.current);
	
	const [tagName, setTagName] = useState<string | undefined>(
		storybookTagName ?? prerenderedName ?? (tagIdentifier ? inferTagName(tagIdentifier) : undefined)
	);
	
	const [count, setCount] = useState(storybookCount ?? prerenderedCount);
	const [tagNotFound, setTagNotFound] = useState(false);
	
	
	// Load tag
	useEffect(() => {
		// Don't fetch if there's no tag identifier
		if (!tagIdentifier) {
			return;
		}
		
		
		// Show the inferred name while the initial fetch is in flight unless the snapshot has the real one
		if (!prerenderData.read<PrerenderedTag>(prerenderKey)) {
			setTagName(inferTagName(tagIdentifier));
		}
		
		setTagNotFound(false);
		
		
		// Restore the prerendered tag name then revalidate in the background
		return prerenderData.revalidate<PrerenderedTag>(
			prerenderKey,
			(data) => setTagName(data.name),
			() =>
				ghost.tags
					.read({ slug: tagIdentifier }, { filter: 'visibility:public' })
					.then((response) => ({ name: response.name })),
			() => setTagNotFound(true)
		);
	}, [tagIdentifier, prerenderKey]);
	
	
	// Handle error
	const handleError = useCallback(
		() =>
			dispatch(
				commonActions.showPageMessage({
					color: 'danger',
					title: 'Oh snap!',
					message: 'There was an unexpected issue.',
				})
			),
		[dispatch]
	);
	
	
	// Handle tag not found
	if (tagNotFound) {
		return <NotFound />;
	}
	
	
	// Return JSX
	return (
		<SimplePageTemplate
			heading={tagName ? `Blog: ${tagName}` : 'Loading…'}
			descriptor={
				<React.Fragment>
					A collection of {count ?? <Skeleton width='2rem' inline />} post{!count || count > 1 ? 's' : ''}
				</React.Fragment>
			}
		>
			<Previews
				handleError={handleError}
				setCount={setCount}
				tagFilter={tagIdentifier ? [tagIdentifier] : []}
				storybookPagination={storybookPagination}
				storybookPosts={storybookPosts}
				initialPage={initialPageRef.current}
				resetKey={tagIdentifier ?? ''}
			/>
		</SimplePageTemplate>
	);
};

export default Tag;
